import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, PackageCheck } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CardTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { fetchClampQcInventoryStatus } from "./clampQcFinishApi";

type ClampQcFinishToStockCardProps = {
  slug: string;
};

const RECEIVE_HINT =
  "Opens the receive page. Leaving this job clocks you out first, then that page posts the failed and passed units and prints one damaged-SKU container for each fail pile.";

export function ClampQcFinishToStockCard({ slug }: ClampQcFinishToStockCardProps) {
  const canWrite = useHubModuleWrite("clampqc");
  const navigate = useNavigate();
  const statusQuery = useQuery({
    queryKey: ["clamp-qc", "inventory-status", slug],
    queryFn: () => fetchClampQcInventoryStatus(slug),
  });
  const stats = statusQuery.data;
  const receivablePassed = stats?.receivablePassedQuantity ?? 0;
  const receivableFailed = stats?.receivableFailedQuantity ?? 0;
  const notInspected = stats?.notInspectedQuantity ?? 0;
  const canReceive = canWrite && receivablePassed + receivableFailed > 0;
  const receiveTo = `/clamp-qc/receive/${encodeURIComponent(slug)}`;

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint={RECEIVE_HINT}>
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageCheck aria-hidden className="h-4 w-4 text-brand-700" />
            Receive work order
          </CardTitle>
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-3 text-sm text-slate-700">
        {statusQuery.isLoading ? (
          <p className="inline-flex items-center gap-2 text-slate-500">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            Loading receive counts…
          </p>
        ) : statusQuery.error ? (
          <p className="text-rose-800">
            {statusQuery.error instanceof ApiError ? statusQuery.error.message : "Could not load receive counts."}
          </p>
        ) : stats != null ? (
          <dl className="grid gap-1 sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Passed</dt>
              <dd className="font-medium tabular-nums text-slate-900">{String(stats.passedCount)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Failed</dt>
              <dd className="font-medium tabular-nums text-slate-900">{String(stats.failedCount)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Not inspected</dt>
              <dd className="font-medium tabular-nums text-slate-900">{String(stats.notInspectedQuantity)}</dd>
            </div>
          </dl>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <HoverHintContent hoverHint="Leaves this job, which clocks you out, then receives on the next page.">
            <Button type="button" disabled={!canReceive} onClick={() => navigate(receiveTo)}>
              {`Receive ${String(receivableFailed)} failed / ${String(receivablePassed)} passed`}
            </Button>
          </HoverHintContent>
          {notInspected > 0 && (stats?.inspectedCount ?? 0) > 0 ? (
            <Button
              type="button"
              variant="secondary"
              disabled={!canWrite}
              onClick={() => navigate(`${receiveTo}?closeShort=1`)}
            >
              Close short
            </Button>
          ) : null}
        </div>
        {!canWrite ? (
          <p className="text-xs text-slate-500">Clamp QC write access is required to receive this work order.</p>
        ) : null}
      </CardBody>
    </Card>
  );
}
